import { calculateUpcomingEventsCutoffDate } from "@/domains/calendar/calculateUpcomingEventsCutoffDate";
import { shouldShowMeeting } from "@/domains/calendar/shouldShowMeeting";
import { Uuid } from "@/domains/global/identifiers";
import { AppStore } from "@/store/AppStore";
import { IndexedSpaceAccountCalendarEventSyncUpdateValue } from "@/store/calendar/space-account-calendar-event/types";
import { DateTime } from "luxon";

const sortCalendarEvents = (
  events: Array<IndexedSpaceAccountCalendarEventSyncUpdateValue>
): Uuid[] => {
  const sortedEvents = events.sort((a, b) => {
    // 1. Sort by start_time
    const aStartTime = a.model_data?.start_time ?? "";
    const bStartTime = b.model_data?.start_time ?? "";
    if (aStartTime !== bStartTime) {
      return aStartTime.localeCompare(bStartTime);
    }
    // 2. Sort by end_time
    const aEndTime = a.model_data?.end_time ?? "";
    const bEndTime = b.model_data?.end_time ?? "";
    if (aEndTime !== bEndTime) {
      return aEndTime.localeCompare(bEndTime);
    }
    // 3. Sort alphabetically by title (primary_label)
    const aTitle = a.model_data?.primary_label ?? "";
    const bTitle = b.model_data?.primary_label ?? "";
    if (aTitle !== bTitle) {
      return aTitle.localeCompare(bTitle);
    }
    // 4. Sort by id as final tiebreaker
    return a.model_id.localeCompare(b.model_id);
  });

  return sortedEvents.map(event => event.model_id) as Uuid[];
};

/**
 * Searches for calendar events within a time range using the indexed start_time field.
 * This is more efficient than using .filter() which loads all events into memory.
 * Results are sorted by: start_time, end_time, alphabetically by title (primary_label), then id.
 */
export const searchCalendarEventsByTimeRange = async ({
  store,
  startTime,
  endTime,
  includeHidden = true,
}: {
  store: AppStore;
  startTime: string;
  endTime: string;
  includeHidden?: boolean;
}): Promise<Uuid[]> => {
  const startUtc = DateTime.fromISO(startTime).toUTC().toISO() ?? "";
  const endUtc = DateTime.fromISO(endTime).toUTC().toISO() ?? "";

  const events = await store.spaceAccountCalendarEvents.localTable
    .where("start_time_utc")
    .between(startUtc, endUtc, true, true)
    .and(
      event =>
        shouldShowMeeting(
          event.model_data?.attendee_count,
          event.model_data?.meet_link,
          store.spaceAccountSettings.calendar.showCalendarEventsWithNoAttendees
        ) &&
        event.model_data?.self_response_status !== "DECLINED" &&
        (includeHidden || event.model_data?.is_hidden !== true)
    )
    .toArray();

  return sortCalendarEvents(events);
};

/**
 * Searches for calendar events that are currently ongoing.
 * An event is considered ongoing if the current time is between
 * the event's start_time and end_time (inclusive start, exclusive end).
 * Results are sorted by: start_time, end_time, alphabetically by title (primary_label), then id.
 */
export const searchOngoingCalendarEvents = async ({
  store,
  includeHidden = true,
  currentTime = DateTime.now(),
}: {
  store: AppStore;
  includeHidden?: boolean;
  currentTime?: DateTime;
}): Promise<Uuid[]> => {
  const currentTimeUtc = currentTime.toUTC().toISO() ?? "";
  const events = await store.spaceAccountCalendarEvents.localTable
    .where("end_time_utc")
    .above(currentTimeUtc)
    .and(event => {
      return (
        event.start_time_utc < currentTimeUtc &&
        shouldShowMeeting(
          event.model_data?.attendee_count,
          event.model_data?.meet_link,
          store.spaceAccountSettings.calendar.showCalendarEventsWithNoAttendees
        ) &&
        event.model_data?.self_response_status !== "DECLINED" &&
        (includeHidden || event.model_data?.is_hidden !== true)
      );
    })
    .toArray();

  return sortCalendarEvents(events);
};

/**
 * Returns the ongoing and upcoming calendar events.
 * Upcoming events includes all events that are +2 weekdays rounded up to end of day
 * i.e. on Friday, it would show up to Tuesday EOD
 * Excludes events that are not meetings unless the user has enabled them, and excludes declined events.
 */
export const getOngoingAndUpcomingCalendarEvents = async ({
  startTime,
  store,
  includeHidden = true,
}: {
  startTime?: DateTime;
  store: AppStore;
  includeHidden?: boolean;
}): Promise<{ ongoingEventIds: Uuid[]; upcomingEventIds: Uuid[] }> => {
  const dateTime = startTime ?? DateTime.now();
  const upcomingMeetingsEnd = calculateUpcomingEventsCutoffDate({ startDate: dateTime });
  const ongoingEventIds = await searchOngoingCalendarEvents({
    store,
    includeHidden,
    currentTime: dateTime,
  });
  const upcomingEventIds = await searchCalendarEventsByTimeRange({
    store,
    startTime: dateTime.toISO() ?? "",
    endTime: upcomingMeetingsEnd.toISO() ?? "",
    includeHidden,
  });

  const ongoingEventSet = new Set(ongoingEventIds);
  const nonOverlappingUpcomingEventIds = upcomingEventIds.filter(
    eventId => !ongoingEventSet.has(eventId)
  );

  return {
    ongoingEventIds,
    upcomingEventIds: nonOverlappingUpcomingEventIds,
  };
};
